import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  BottomNavigation,
  BottomNavigationAction,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  School,
  People,
  Logout,
  Settings,
  SyncAlt,
  History,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getUserInitials } from '../utils/format';
import { useSystemSettings } from './DynamicThemeProvider';

const drawerWidth = 260;

interface NavItem {
  title: string;
  icon: React.ReactElement;
  path: string;
  roles?: string[];
}

const navItems: NavItem[] = [
  { title: 'Dashboard', icon: <Dashboard />, path: '/' },
  {
    title: 'Cursos',
    icon: <School />,
    path: '/courses',
    roles: ['admin', 'coordinator', 'teacher'],
  },
  { title: 'Estudiantes', icon: <People />, path: '/students' },
  { title: 'Usuarios', icon: <People />, path: '/users', roles: ['admin'] },
  {
    title: 'Sincronización Moodle',
    icon: <SyncAlt />,
    path: '/moodle-sync',
    roles: ['admin', 'coordinator'],
  },
  {
    title: 'Auditoría',
    icon: <History />,
    path: '/audit-logs',
    roles: ['admin'],
  },
];

const MainLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSystemSettings();


  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const filteredNavItems = navItems.filter((item) => {
    if (user?.role === 'student') {
      return item.path === '/';
    }
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  const drawer = (
    <Box>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3 }}>
        {settings.logo ? (
          <Avatar 
            src={settings.logo} 
            variant="rounded" 
            sx={{ width: 36, height: 36, objectFit: 'contain', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', p: 0.2 }}
          />
        ) : (
          <School color="primary" sx={{ fontSize: 32 }} />
        )}
        <Typography variant="h6" noWrap component="div" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
          {settings.institution_name}
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ px: 2, py: 1 }}>
        {filteredNavItems.map((item) => {
          const isActive = item.path === '/' 
            ? location.pathname === '/' 
            : location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.title} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  borderRadius: 2,
                  bgcolor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'primary.contrastText' : 'text.primary',
                  '& .MuiListItemIcon-root': {
                    color: isActive ? 'primary.contrastText' : 'text.secondary',
                    minWidth: 40,
                    transition: 'color 0.2s',
                  },
                  '& .MuiListItemText-primary': {
                    color: isActive ? 'primary.contrastText' : 'text.primary',
                    fontWeight: isActive ? 700 : 500,
                    transition: 'color 0.2s',
                  },
                  '&:hover': {
                    bgcolor: isActive ? 'primary.dark' : 'primary.light',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'primary.contrastText',
                    },
                  },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'primary.contrastText',
                    },
                  },
                  transition: 'all 0.2s',
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
        elevation={1}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {/* Page title can be added here */}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {user && (
              <>
                <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
                  {user.first_name} {user.last_name}
                </Typography>
                <IconButton onClick={handleMenuOpen}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                    {getUserInitials(user)}
                  </Avatar>
                </IconButton>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {user?.role === 'admin' && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate('/settings');
            }}
          >
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            Configuración
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          Cerrar Sesión
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          mb: { xs: 8, sm: 0 }, // Margin at bottom for mobile nav bar padding
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Box key={location.pathname} className="fade-in-up">
          <Outlet />
        </Box>
      </Box>

      {/* Mobile App fixed Bottom Navigation Bar */}
      <BottomNavigation
        value={location.pathname === '/settings' ? '/settings' : location.pathname}
        onChange={(_, newValue) => {
          navigate(newValue);
        }}
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: 64,
          borderTop: '1px solid #E2E8F0',
          boxShadow: '0 -4px 16px rgba(10, 37, 64, 0.05)',
          bgcolor: 'background.paper',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 0',
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'secondary.main',
              fontWeight: 700
            }
          }
        }}
      >
        <BottomNavigationAction label="Inicio" value="/" icon={<Dashboard />} />
        
        {user?.role !== 'student' && (
          <BottomNavigationAction label="Alumnos" value="/students" icon={<People />} />
        )}
        
        {user?.role === 'admin' && (
          <BottomNavigationAction label="Usuarios" value="/users" icon={<People />} />
        )}
        
        {user && ['admin', 'coordinator'].includes(user.role) && (
          <BottomNavigationAction label="Moodle" value="/moodle-sync" icon={<SyncAlt />} />
        )}
        
        {user?.role === 'admin' && (
          <BottomNavigationAction label="Ajustes" value="/settings" icon={<Settings />} />
        )}
      </BottomNavigation>
    </Box>
  );
};

export default MainLayout;
